import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Camera, ChefHat, UtensilsCrossed } from 'lucide-react';
import { AIUsageService, AIFeatureType } from '../../lib/aiUsageService';
import { supabase } from '../../lib/supabase';

interface FeatureUsageData {
  used: number;
  limit: number;
  plan_type: string;
  resets_on: string;
  can_use: boolean;
  reason: string;
}

interface UsageStats {
  image_scan: FeatureUsageData | null;
  recipe_generation: FeatureUsageData | null;
  meal_analysis: FeatureUsageData | null;
}

const AIUsageSection = () => {
  const [usageStats, setUsageStats] = useState<UsageStats>({
    image_scan: null,
    recipe_generation: null,
    meal_analysis: null
  });
  const [loading, setLoading] = useState(true);

  // Feature configuration
  const features = [
    {
      key: 'image_scan' as AIFeatureType,
      icon: Camera,
      title: 'Image Scanning',
      description: 'Photo analysis for food identification'
    },
    {
      key: 'recipe_generation' as AIFeatureType,
      icon: ChefHat,
      title: 'Recipe Generation',
      description: 'AI-powered recipe creation'
    },
    {
      key: 'meal_analysis' as AIFeatureType,
      icon: UtensilsCrossed,
      title: 'Meal Analysis',
      description: 'Nutrition tracking and meal logging'
    }
  ];

  useEffect(() => {
    const fetchAllUsageData = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) return;

        // Fetch usage data for each feature
        const statsPromises = features.map(async (feature) => {
          try {
            const limits = await AIUsageService.checkFeatureLimit(user.id, feature.key);
            return { feature: feature.key, data: limits };
          } catch (error) {
            console.error(`Error fetching ${feature.key} usage:`, error);
            return { feature: feature.key, data: null };
          }
        });

        const results = await Promise.all(statsPromises);
        
        // Transform results into usageStats format
        const newStats: UsageStats = {
          image_scan: null,
          recipe_generation: null,
          meal_analysis: null
        };

        results.forEach(({ feature, data }) => {
          if (data) {
            newStats[feature] = data;
          }
        });

        setUsageStats(newStats);
      } catch (error) {
        console.error('Error in fetchAllUsageData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsageData();
  }, []);

  // Calculate days until reset
  const getDaysUntilReset = (resetDate: string): string => {
    const now = new Date();
    const reset = new Date(resetDate);
    const diffTime = reset.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Resets today';
    if (diffDays === 1) return 'Resets tomorrow';
    return `Resets in ${diffDays} days`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg shadow-black/10">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
          </div>
          
          {/* Feature items skeleton */}
          {[1, 2, 3].map((i) => (
            <div key={i} className="mb-6 last:mb-0">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div>
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-1"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                  </div>
                </div>
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
              </div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
              <div className="flex justify-between">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Individual feature card component
  const UsageStatsCard: React.FC<{
    feature: typeof features[0];
    data: FeatureUsageData | null;
  }> = ({ feature, data }) => {
    if (!data) {
      return (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <feature.icon size={18} className="text-gray-400" />
            <h4 className="text-sm font-semibold text-gray-500">
              {feature.title}
            </h4>
          </div>
          <div className="text-center py-2">
            <p className="text-xs text-gray-400">No data</p>
          </div>
        </div>
      );
    }

    const progress = data.limit > 0 ? data.used / data.limit : 0;
    const isAtLimit = progress >= 1;
    const isNearLimit = progress >= 0.8;

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700">
        {/* Feature Header */}
        <div className="flex items-center gap-2 mb-3">
          <feature.icon size={18} className="text-gray-600 dark:text-gray-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
            {feature.title}
          </h4>
        </div>

        {/* Plan Badge */}
        <div className="mb-3">
          <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${
            data.plan_type === 'free' 
              ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              : data.plan_type === 'pro'
                ? 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300'
                : 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300'
          }`}>
            {data.plan_type} Plan
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(progress * 100, 100)}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`h-2 rounded-full ${
              isAtLimit 
                ? 'bg-red-500' 
                : isNearLimit 
                  ? 'bg-yellow-500' 
                  : 'bg-orange-500'
            }`}
          />
        </div>

        {/* Usage Numbers */}
        <div className="text-center mb-2">
          <span className={`text-lg font-bold ${
            isAtLimit 
              ? 'text-red-600 dark:text-red-400' 
              : 'text-gray-900 dark:text-white'
          }`}>
            {data.used}
          </span>
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            /{data.limit}
          </span>
        </div>

        {/* Reset Date */}
        <div className="text-center">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {getDaysUntilReset(data.resets_on)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header with Lightning Bolt */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center">
          <Zap size={18} className="text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
          AI Usage This Month
        </h3>
      </div>

      {/* Three Cards in a Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((feature) => (
          <UsageStatsCard
            key={feature.key}
            feature={feature}
            data={usageStats[feature.key]}
          />
        ))}
      </div>

      {/* Show message if no data */}
      {!Object.values(usageStats).some(data => data !== null) && (
        <div className="text-center py-6 mt-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <p className="text-gray-500 dark:text-gray-400">
            No usage data available
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default AIUsageSection;